import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Paper, 
  InputBase,
  Checkbox,
  FormControlLabel,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Chip,
  Badge,
  Grid
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';

const AssignDegreesModal = ({
  showAssignDegreesModal,
  departments,
  handleCancelAssignDegreesModal,
  assignDegrees,
  selectedDegrees,
  setSelectedDegrees,
  getDegrees,
  degrees,
  setDegrees,
}) => {
  const [searchKeyword, setSearchKeyword] = useState('');
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [filteredDepartments, setFilteredDepartments] = useState([]);
  const [programLevel, setProgramLevel] = useState('All');
  const [selectedProgramsExpanded, setSelectedProgramsExpanded] = useState(false);
  const [cachedDegrees, setCachedDegrees] = useState(null);
  
  // New state for campus and faculty filters
  const [campuses, setCampuses] = useState([]);
  const [faculties, setFaculties] = useState([]);
  const [selectedCampus, setSelectedCampus] = useState('All');
  const [selectedFaculty, setSelectedFaculty] = useState('All');
  const [isLoadingFaculties, setIsLoadingFaculties] = useState(false);
  
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const token = sessionStorage.getItem("token");
  // Use ref to track if we've already fetched degrees
  const hasInitiallyFetched = useRef(false);
  // Use ref to track the previous search term for debounce checking
  const prevSearchRef = useRef('');
  const prevProgramLevelRef = useRef('All');
  const prevCampusRef = useRef('All');
  const prevFacultyRef = useRef('All');
  
  // Use ref to store the debounce timer
  const searchDebounceTimer = useRef(null);

  // Program level options
  const programLevels = [
    'All',
    'Undergraduate (BS Programs)', 
    'Graduate (MS, M-Phil Programs)', 
    'Doctorate (PhD Programs)'
  ];

  // Fetch campuses when modal opens
  useEffect(() => {
    if (showAssignDegreesModal) {
      fetchCampuses();
    }
  }, [showAssignDegreesModal]);

  // Fetch campuses from API using fetch instead of axios
  const fetchCampuses = async () => {
    try {
      const response = await fetch(`${BASE_URL}/campus/get_campuses`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      if (data.campuses && data.campuses.length > 0) {
        setCampuses(data.campuses);
      }
    } catch (error) {
      console.error("Error fetching campuses:", error);
    }
  };

  // Fetch faculties when campus changes
  useEffect(() => {
    if (selectedCampus && selectedCampus !== 'All') {
      fetchFaculties(selectedCampus);
    } else {
      setFaculties([]);
      setSelectedFaculty('All');
    }
  }, [selectedCampus]);

  // Fetch faculties by campus ID using fetch instead of axios
  const fetchFaculties = async (campusId) => {
    if (campusId === 'All') return;
    
    setIsLoadingFaculties(true);
    try {
      const response = await fetch(`${BASE_URL}/faculty/get_faculties_by_campus/${campusId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      if (data.faculties && data.faculties.length > 0) {
        setFaculties(data.faculties);
      } else {
        setFaculties([]);
      }
    } catch (error) {
      console.error("Error fetching faculties:", error);
      setFaculties([]);
    } finally {
      setIsLoadingFaculties(false);
    }
  };

  // Initial setup when modal opens - fetch degrees once
  useEffect(() => {
    if (showAssignDegreesModal) {
      // Only fetch degrees if we haven't already done so and don't have them cached
      if (!hasInitiallyFetched.current && (!cachedDegrees || cachedDegrees.length === 0)) {
        getDegrees();
        hasInitiallyFetched.current = true;
      }
      
      // Set initial filteredDepartments
      filterDepartments(programLevel, searchKeyword, selectedCampus, selectedFaculty);
    } else {
      // Reset state when modal closes
      setSearchKeyword('');
      setProgramLevel('All');
      setSelectedCampus('All');
      setSelectedFaculty('All');
      prevSearchRef.current = '';
      prevProgramLevelRef.current = 'All';
      prevCampusRef.current = 'All';
      prevFacultyRef.current = 'All';
    }
  }, [showAssignDegreesModal, getDegrees]);

  // Cache degrees when they're fetched
  useEffect(() => {
    if (degrees && degrees.length > 0) {
      setCachedDegrees(degrees);
    }
  }, [degrees]);

  // Main filter function that handles both search and filters
  const filterDepartments = useCallback((level, search, campus, faculty) => {
    const searchTerm = search.toLowerCase().trim();
    
    // Apply all filters
    const filtered = departments.map(dept => {
      // Make a copy of the department to avoid modifying the original
      const deptCopy = {...dept};
      
      // Filter programs by all criteria
      let matchingPrograms = [...dept.programs];
      
      // 1. Filter by program level if not 'All'
      if (level !== 'All') {
        matchingPrograms = matchingPrograms.filter(program => 
          program.program_level === level
        );
      }
      
      // 2. Filter by campus if not 'All'
      if (campus !== 'All') {
        matchingPrograms = matchingPrograms.filter(program => 
          program.campus_id === parseInt(campus)
        );
      }
      
      // 3. Filter by faculty if not 'All'
      if (faculty !== 'All') {
        matchingPrograms = matchingPrograms.filter(program => 
          program.faculty_id === parseInt(faculty)
        );
      }
      
      // If no programs match the filters, return null
      if (matchingPrograms.length === 0) {
        return null;
      }
      
      // Return department with filtered programs
      return {
        ...deptCopy,
        programs: matchingPrograms
      };
    }).filter(Boolean); // Remove null departments
    
    // Apply search filter if needed
    if (!searchTerm) {
      setFilteredDepartments(filtered);
    } else {
      const searchFiltered = filtered.map(dept => {
        const isDepartmentMatch = dept.name.toLowerCase().includes(searchTerm);
        
        const matchingPrograms = dept.programs.filter(program => 
          program.name.toLowerCase().includes(searchTerm)
        );
        
        if (isDepartmentMatch || matchingPrograms.length > 0) {
          return {
            ...dept,
            programs: isDepartmentMatch ? dept.programs : matchingPrograms,
            matchesDepartment: isDepartmentMatch
          };
        }
        return null;
      }).filter(Boolean);
      
      setFilteredDepartments(searchFiltered);
    }
    
    // 3. Apply the same filtering to degrees if we have cached data
    if (cachedDegrees) {
      let filteredDegrees = [...cachedDegrees];
      
      // Apply level filter to degrees
      if (level !== 'All') {
        filteredDegrees = filteredDegrees.filter(deg => {
          for (const dept of departments) {
            const matchingProgram = dept.programs.find(prog => prog.id === deg.id);
            if (matchingProgram && matchingProgram.program_level === level) {
              return true;
            }
          }
          return false;
        });
      }
      
      // Apply campus filter to degrees
      if (campus !== 'All') {
        filteredDegrees = filteredDegrees.filter(deg => {
          for (const dept of departments) {
            const matchingProgram = dept.programs.find(
              prog => prog.id === deg.id && prog.campus_id === parseInt(campus)
            );
            if (matchingProgram) {
              return true;
            }
          }
          return false;
        });
      }
      
      // Apply faculty filter to degrees
      if (faculty !== 'All') {
        filteredDegrees = filteredDegrees.filter(deg => {
          for (const dept of departments) {
            const matchingProgram = dept.programs.find(
              prog => prog.id === deg.id && prog.faculty_id === parseInt(faculty)
            );
            if (matchingProgram) {
              return true;
            }
          }
          return false;
        });
      }
      
      // Apply search filter to degrees
      if (searchTerm) {
        filteredDegrees = filteredDegrees.filter(deg => 
          deg.name && deg.name.toLowerCase().includes(searchTerm)
        );
      }
      
      // Update degrees state
      setDegrees(filteredDegrees);
    }
  }, [departments, cachedDegrees, setDegrees]);

  // Debounced search handler
  const handleSearchChange = useCallback((e) => {
    const newSearchTerm = e.target.value;
    setSearchKeyword(newSearchTerm);
    
    // Clear any existing timer
    if (searchDebounceTimer.current) {
      clearTimeout(searchDebounceTimer.current);
    }
    
    // Set new timer
    searchDebounceTimer.current = setTimeout(() => {
      if (newSearchTerm !== prevSearchRef.current) {
        filterDepartments(programLevel, newSearchTerm, selectedCampus, selectedFaculty);
        prevSearchRef.current = newSearchTerm;
      }
    }, 300);
  }, [programLevel, selectedCampus, selectedFaculty, filterDepartments]);

  // Program level change handler
  const handleProgramLevelChange = useCallback((e) => {
    const newLevel = e.target.value;
    setProgramLevel(newLevel);
    
    if (newLevel !== prevProgramLevelRef.current) {
      filterDepartments(newLevel, searchKeyword, selectedCampus, selectedFaculty);
      prevProgramLevelRef.current = newLevel;
    }
  }, [searchKeyword, selectedCampus, selectedFaculty, filterDepartments]);

  // Campus change handler
  const handleCampusChange = useCallback((e) => {
    const newCampus = e.target.value;
    setSelectedCampus(newCampus);
    setSelectedFaculty('All'); // Reset faculty when campus changes
    
    if (newCampus !== prevCampusRef.current) {
      filterDepartments(programLevel, searchKeyword, newCampus, 'All');
      prevCampusRef.current = newCampus;
      prevFacultyRef.current = 'All';
    }
  }, [programLevel, searchKeyword, filterDepartments]);

  // Faculty change handler
  const handleFacultyChange = useCallback((e) => {
    const newFaculty = e.target.value;
    setSelectedFaculty(newFaculty);
    
    if (newFaculty !== prevFacultyRef.current) {
      filterDepartments(programLevel, searchKeyword, selectedCampus, newFaculty);
      prevFacultyRef.current = newFaculty;
    }
  }, [programLevel, searchKeyword, selectedCampus, filterDepartments]);

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceTimer.current) {
        clearTimeout(searchDebounceTimer.current);
      }
    };
  }, []);

  // Handle select all departments and programs
  const handleSelectAll = useCallback((e) => {
    const { checked } = e.target;
    setSelectAll(checked);

    if (checked) {
      // Only select departments and programs that are currently filtered/visible
      const filteredDepartmentIds = filteredDepartments.map(dept => dept.id);
      const filteredPrograms = filteredDepartments.flatMap(dept => 
        dept.programs.map(program => ({
          degree_id: program.id,
          test_required: 1,
          seats: 0
        }))
      );

      setSelectedDepartments(filteredDepartmentIds);
      setSelectedDegrees(filteredPrograms);
    } else {
      setSelectedDepartments([]);
      setSelectedDegrees([]);
    }
  }, [filteredDepartments, setSelectedDegrees]);

  // Handle department selection/deselection
  const handleDepartmentToggle = useCallback((department) => {
    setSelectedDepartments(prev => {
      const isDepartmentSelected = prev.includes(department.id);
      
      if (isDepartmentSelected) {
        // Remove department
        const newDepts = prev.filter(deptId => deptId !== department.id);
        
        // Remove all programs from this department
        setSelectedDegrees(prevDegrees => 
          prevDegrees.filter(deg => 
            !department.programs.some(prog => prog.id === deg.degree_id)
          )
        );
        
        return newDepts;
      } else {
        // Add department and all its programs
        setSelectedDegrees(prevDegrees => {
          const existingDegreeIds = new Set(prevDegrees.map(d => d.degree_id));
          const newPrograms = department.programs
            .filter(prog => !existingDegreeIds.has(prog.id))
            .map(program => ({
              degree_id: program.id,
              test_required: 1,
              seats: 0
            }));
            
          return [...prevDegrees, ...newPrograms];
        });
        
        return [...prev, department.id];
      }
    });
  }, [setSelectedDegrees]);

  // Handle individual program selection/deselection
  const handleProgramToggle = useCallback((program, department) => {
    setSelectedDegrees(prevDegrees => {
      const isProgramSelected = prevDegrees.some(
        deg => deg.degree_id === program.id
      );
      
      if (isProgramSelected) {
        // Remove program
        const newDegrees = prevDegrees.filter(deg => deg.degree_id !== program.id);
        
        // Check if department should still be selected
        const hasOtherProgramsSelected = department.programs.some(
          prog => prog.id !== program.id && newDegrees.some(deg => deg.degree_id === prog.id)
        );
        
        if (!hasOtherProgramsSelected) {
          setSelectedDepartments(prev => prev.filter(id => id !== department.id));
        }
        
        return newDegrees;
      } else {
        // Add program
        const newDegree = { 
          degree_id: program.id, 
          test_required: 1, 
          seats: 0 
        };
        
        // Ensure department is selected
        if (!selectedDepartments.includes(department.id)) {
          setSelectedDepartments(prev => [...prev, department.id]);
        }
        
        return [...prevDegrees, newDegree];
      }
    });
  }, [selectedDepartments, setSelectedDepartments]);

  // Remove program from selected degrees
  const handleRemoveProgram = useCallback((degreeId) => {
    setSelectedDegrees(prevDegrees => {
      const newDegrees = prevDegrees.filter(deg => deg.degree_id !== degreeId);
      
      // Also update selected departments if needed
      const departmentToUpdate = departments.find(dept => 
        dept.programs.some(prog => prog.id === degreeId)
      );
      
      if (departmentToUpdate) {
        const hasOtherProgramsSelected = departmentToUpdate.programs.some(
          prog => prog.id !== degreeId && newDegrees.some(deg => deg.degree_id === prog.id)
        );
        
        if (!hasOtherProgramsSelected) {
          setSelectedDepartments(prev => prev.filter(id => id !== departmentToUpdate.id));
        }
      }
      
      return newDegrees;
    });
  }, [departments]);

  // Handle confirmation
  const handleConfirm = useCallback(() => {
    assignDegrees(selectedDegrees);
    handleCancelAssignDegreesModal();
  }, [assignDegrees, selectedDegrees, handleCancelAssignDegreesModal]);

  // Reset search and filters when modal closes
  const handleModalClose = useCallback(() => {
    setSearchKeyword('');
    setProgramLevel('All');
    setSelectedCampus('All');
    setSelectedFaculty('All');
    handleCancelAssignDegreesModal();
  }, [handleCancelAssignDegreesModal]);

  // Get program details from ID
  const getProgramDetails = useCallback((degreeId) => {
    for (const department of departments) {
      const program = department.programs.find(prog => prog.id === degreeId);
      if (program) {
        return {
          program,
          department
        };
      }
    }
    return { program: { name: 'Unknown Program' }, department: { name: 'Unknown Department' } };
  }, [departments]);

  // Get campus name from ID
  const getCampusName = useCallback((campusId) => {
    const campus = campuses.find(c => c.id === campusId);
    return campus ? campus.name : 'Unknown Campus';
  }, [campuses]);

  // Get faculty name from ID
  const getFacultyName = useCallback((facultyId) => {
    const faculty = faculties.find(f => f.id === facultyId);
    return faculty ? faculty.name : 'Unknown Faculty';
  }, [faculties]);

  // Group selected programs by department for the cart
  const selectedProgramsByDepartment = useMemo(() => {
    const groupedPrograms = {};
    
    selectedDegrees.forEach(deg => {
      const { program, department } = getProgramDetails(deg.degree_id);
      
      if (!groupedPrograms[department.id]) {
        groupedPrograms[department.id] = {
          departmentName: department.name,
          programs: []
        };
      }
      
      groupedPrograms[department.id].programs.push({
        id: program.id,
        name: program.name,
        program_level: program.program_level,
        campus_id: program.campus_id,
        faculty_id: program.faculty_id
      });
    });
    
    return Object.values(groupedPrograms);
  }, [selectedDegrees, getProgramDetails]);

  // Render selected programs in the cart
  const renderSelectedPrograms = useMemo(() => {
    if (selectedDegrees.length === 0) {
      return (
        <Typography variant="body1" sx={{ padding: 2 }}>
          No programs selected
        </Typography>
      );
    }

    return (
      <div className="selected-programs-container">
        {selectedProgramsByDepartment.map((deptGroup) => (
          <div key={`selected-dept-${deptGroup.departmentName}`} className="department-group mb-3">
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
              {deptGroup.departmentName}
            </Typography>
            <div className="programs-chip-container">
              {deptGroup.programs.map((prog) => {
                // Get campus and faculty names if available
                const campusName = prog.campus_id ? getCampusName(prog.campus_id) : '';
                const facultyName = prog.faculty_id ? getFacultyName(prog.faculty_id) : '';
                
                // Create label with program info
                const chipLabel = (
                  <span>
                    {prog.name}
                    <span style={{ fontSize: '0.8em', marginLeft: '4px', opacity: 0.7 }}>
                      ({prog.program_level?.split(' ')[0]})
                    </span>
                  </span>
                );
                
                return (
                  <Chip
                    key={`selected-prog-${prog.id}`}
                    label={chipLabel}
                    onDelete={() => handleRemoveProgram(prog.id)}
                    sx={{ margin: '4px', maxWidth: '100%' }}
                  />
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  }, [selectedProgramsByDepartment, selectedDegrees.length, handleRemoveProgram, getCampusName, getFacultyName]);

  // Update filtered departments when modal opens
  useEffect(() => {
    if (showAssignDegreesModal && departments.length > 0) {
      setFilteredDepartments(departments);
    }
  }, [showAssignDegreesModal, departments]);

  // Render departments and programs efficiently
  const renderDepartmentsAndPrograms = useMemo(() => {
    if (filteredDepartments.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={1} align="center">
            No matching departments or programs found
          </TableCell>
        </TableRow>
      );
    }

    return filteredDepartments.map((department) => (
      <React.Fragment key={`dept-${department.id}`}>
        {/* Department row */}
        <TableRow sx={{ backgroundColor: department.matchesDepartment ? '#f5f5f5' : 'inherit' }}>
          <TableCell>
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedDepartments.includes(department.id)}
                  onChange={() => handleDepartmentToggle(department)}
                  id={`department-checkbox-${department.id}`}
                  className="me-2"
                />
              }
              label={<strong>{department.name}</strong>}
            />
          </TableCell>
        </TableRow>
        
        {/* Program rows */}
        {department.programs.map((program) => {
          // Get campus and faculty names
          const campusName = program.campus_id ? getCampusName(program.campus_id) : '';
          const facultyName = program.faculty_id ? getFacultyName(program.faculty_id) : '';
          
          return (
            <TableRow key={`program-${program.id}`} className="bg-light">
              <TableCell sx={{ paddingLeft: 5 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedDegrees.some(deg => deg.degree_id === program.id)}
                      onChange={() => handleProgramToggle(program, department)}
                      id={`program-checkbox-${program.id}`}
                      className="me-2"
                    />
                  }
                  label={
                    <span>
                      {program.name}
                      <span className="text-muted ms-2" style={{ fontSize: '0.85em' }}>
                        ({program.program_level})
                      </span>
                    </span>
                  }
                />
              </TableCell>
            </TableRow>
          );
        })}
      </React.Fragment>
    ));
  }, [filteredDepartments, selectedDepartments, selectedDegrees, handleDepartmentToggle, handleProgramToggle, getCampusName, getFacultyName]);

  return (
    <Dialog
      open={showAssignDegreesModal}
      onClose={handleModalClose}
      aria-labelledby="assign-degrees-dialog-title"
      maxWidth="xl"
      fullWidth
      PaperProps={{
        className: "dialog-assign",
      }}
    >
      <DialogTitle id="assign-degrees-dialog-title" className="dialog-title">
        Assign Programs
      </DialogTitle>

      <DialogContent className="dialog-content">
        {/* Selected Programs Accordion */}
        <Accordion 
          expanded={selectedProgramsExpanded} 
          onChange={() => setSelectedProgramsExpanded(!selectedProgramsExpanded)}
          sx={{ mb: 3 }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="selected-programs-content"
            id="selected-programs-header"
            sx={{ backgroundColor: '#f5f5f5' }}
          >
            <Badge badgeContent={selectedDegrees.length} color="primary" sx={{ mr: 2 }}>
              <ShoppingCartIcon color="action" />
            </Badge>
            <Typography variant="h6">
              Selected Programs ({selectedDegrees.length})
            </Typography>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              maxHeight: '200px',
              overflowY: 'auto',
              padding: 2
            }}
          >
            {renderSelectedPrograms}
          </AccordionDetails>
        </Accordion>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          {/* Search input */}
          <Grid item xs={12} md={6}>
            <Paper className="search-paper-assign">
              <InputBase
                sx={{ ml: 1, flex: 1 }}
                placeholder="Search departments and programs..."
                inputProps={{ "aria-label": "search departments and programs" }}
                value={searchKeyword}
                onChange={handleSearchChange}
              />
              <SearchIcon className="search-icon" />
            </Paper>
          </Grid>
          
          {/* Campus filter */}
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel id="campus-select-label"><b>Campus</b></InputLabel>
              <Select
                labelId="campus-select-label"
                id="campus-select"
                value={selectedCampus}
                label="Campus"
                onChange={handleCampusChange}
                style={{ height: "40px" }}
              >
                <MenuItem value="All">All Campuses</MenuItem>
                {campuses.map((campus) => (
                  <MenuItem key={`campus-${campus.id}`} value={campus.id.toString()}>
                    {campus.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          {/* Faculty filter */}
          <Grid item xs={12} md={3}>
            <FormControl fullWidth disabled={selectedCampus === 'All' || isLoadingFaculties}>
              <InputLabel id="faculty-select-label"><b>Faculty</b></InputLabel>
              <Select
                labelId="faculty-select-label"
                id="faculty-select"
                value={selectedFaculty}
                label="Faculty"
                onChange={handleFacultyChange}
                style={{ height: "40px" }}
              >
                <MenuItem value="All">All Faculties</MenuItem>
                {faculties.map((faculty) => (
                  <MenuItem key={`faculty-${faculty.id}`} value={faculty.id.toString()}>
                    {faculty.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          {/* Program level filter */}
          <Grid item xs={12} md={9}>
            <FormControl fullWidth>
              <InputLabel id="program-level-label">Program Level</InputLabel>
              <Select
                labelId="program-level-label"
                id="program-level-select"
                value={programLevel}
                label="Program Level"
                onChange={handleProgramLevelChange}
                style={{ height: "40px" }}
              >
                {programLevels.map((level) => (
                  <MenuItem key={level} value={level}>
                    {level}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          {/* Select all checkbox */}
          <Grid item xs={12} md={3}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectAll}
                  onChange={handleSelectAll}
                  id="selectAllCheckbox"
                />
              }
              label="Select All"
              sx={{ mt: 1 }}
            />
          </Grid>
        </Grid>

        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Department/Program</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {renderDepartmentsAndPrograms}
          </TableBody>
        </Table>
      </DialogContent>

      <DialogActions>
        <Button
          className="dialog-actions-button negative"
          onClick={handleModalClose}
        >
          Cancel
        </Button>
        <Button
          className="dialog-actions-button positive"
          onClick={handleConfirm}
          disabled={selectedDegrees.length === 0}
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignDegreesModal;